import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link, useParams } from "wouter";
import { useEffect } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Users,
  Globe,
  CheckCircle2,
  ChevronRight
} from "lucide-react";
import { getProjectDetail } from "@/data/projects";
import NotFound from "./not-found";

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const project = getProjectDetail(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) return <NotFound />;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-20 h-[70vh] min-h-[500px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={project.heroImage}
            alt={language === 'ar' ? project.title.ar : project.title.en}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
        </div>

        {/* Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute top-8 left-4 sm:left-6 lg:left-8"
          >
            <Link href="/projects">
              <Button variant="secondary" className={`gap-2 backdrop-blur-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                {isRTL ? 'جميع المشاريع' : 'All Projects'}
              </Button>
            </Link>
          </motion.div>

          <motion.div
            className={`max-w-3xl ${isRTL ? 'text-right ml-auto' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Category Badge */}
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium bg-primary/20 text-primary backdrop-blur-sm mb-4">
              {language === 'ar' ? project.category.ar : project.category.en}
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              {language === 'ar' ? project.title.ar : project.title.en}
            </h1>

            <p className="text-xl text-muted-foreground leading-relaxed">
              {language === 'ar' ? project.description.ar : project.description.en}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Project Info Bar */}
      <section className="py-8 bg-muted/30 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 ${isRTL ? 'text-right' : ''}`}>
            <div>
              <div className="text-sm text-muted-foreground mb-1">
                {isRTL ? 'العميل' : 'Client'}
              </div>
              <div className="font-semibold">
                {language === 'ar' ? project.client.ar : project.client.en}
              </div>
            </div>
            <div>
              <div className={`text-sm text-muted-foreground mb-1 flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                <Calendar className="h-4 w-4" />
                {isRTL ? 'المدة' : 'Duration'}
              </div>
              <div className="font-semibold">
                {language === 'ar' ? project.duration.ar : project.duration.en}
              </div>
            </div>
            <div>
              <div className={`text-sm text-muted-foreground mb-1 flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                <Users className="h-4 w-4" />
                {isRTL ? 'الفريق' : 'Team'}
              </div>
              <div className="font-semibold">
                {language === 'ar' ? project.teamSize.ar : project.teamSize.en}
              </div>
            </div>
            <div>
              <div className={`text-sm text-muted-foreground mb-1 flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                <Globe className="h-4 w-4" />
                {isRTL ? 'المنصة' : 'Platform'}
              </div>
              <div className="font-semibold">
                {language === 'ar' ? project.platform.ar : project.platform.en}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Challenge */}
            <motion.div
              className={isRTL ? 'text-right' : ''}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
                {isRTL ? 'التحدي' : 'The Challenge'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                {isRTL ? 'ما كان يحتاجه العميل' : 'What the client needed'}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {language === 'ar' ? project.challenge.ar : project.challenge.en}
              </p>
            </motion.div>

            {/* Solution */}
            <motion.div
              className={isRTL ? 'text-right' : ''}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="inline-block text-sm font-medium text-secondary uppercase tracking-wider mb-4">
                {isRTL ? 'الحل' : 'The Solution'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                {isRTL ? 'كيف حللنا المشكلة' : 'How we solved it'}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {language === 'ar' ? project.solution.ar : project.solution.en}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className={`text-lg font-semibold mb-6 ${isRTL ? 'text-right' : ''}`}>
            {isRTL ? 'التقنيات المستخدمة' : 'Technologies Used'}
          </h3>
          <div className={`flex flex-wrap gap-3 ${isRTL ? 'justify-end' : ''}`}>
            {project.technologies.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 rounded-xl bg-card border border-border/50 text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className={`mb-12 ${isRTL ? 'text-right' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
              {isRTL ? 'الميزات' : 'Features'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              {isRTL ? 'ما يتضمنه المشروع' : 'What we built'}
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {(language === 'ar' ? project.features.ar : project.features.en).map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`flex items-start gap-3 p-6 rounded-xl bg-card border border-border/50 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
              >
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="font-medium">{feature}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
              {isRTL ? 'النتائج' : 'Results'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              {isRTL ? 'الأثر الذي أحدثناه' : 'The impact we made'}
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {(language === 'ar' ? project.results.ar : project.results.en).map((result, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center p-6 rounded-xl bg-card border border-border/50"
              >
                <div className="text-3xl font-bold text-primary mb-2">
                  {result.split(' ')[0]}
                </div>
                <div className="text-muted-foreground">
                  {result.split(' ').slice(1).join(' ')}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            {project.prevProject ? (
              <Link href={`/projects/${project.prevProject}`}>
                <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                  {isRTL ? 'المشروع السابق' : 'Previous Project'}
                </Button>
              </Link>
            ) : <div />}

            {project.nextProject && (
              <Link href={`/projects/${project.nextProject}`}>
                <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {isRTL ? 'المشروع التالي' : 'Next Project'}
                  <ArrowRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {isRTL ? 'مستعد لمشروعك القادم؟' : 'Ready for your next project?'}
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {isRTL
                ? 'دعنا نناقش كيف يمكننا مساعدتك في تحقيق أهدافك'
                : "Let's discuss how we can help you achieve your goals"}
            </p>
            <Link href="/#contact">
              <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-xl">
                {t("getInTouch")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
